import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Banknote, CalendarRange, HandCoins, MapPinned, Package } from 'lucide-react';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { DETAIL_TABLE_WIDER_DATES_HINT } from '@/components/common/detailTableLayout.constants';
import { LabeledPillList, type PillField } from '@/components/common/LabeledPill';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { formatDisplayDate, getMonthToDateRange } from '@/utils/date';
import { getTableEmptyText } from '@/utils/listSearch';
import { formatNumber } from '@/utils/number';
import {
  mpRegionDonationsInfiniteQueryOptions,
  mpRegionProjectsQueryOptions,
  mpRegionsQueryOptions,
} from '../api/marketPromotion.api';
import {
  getRegionDonationColumns,
  REGION_DONATION_ZM_COLUMN_ID,
} from '../components/RegionDonationColumns';
import {
  DONATION_SEARCH_PLACEHOLDER,
  REGION_REPORT_PATH,
  REGION_REPORT_TAB,
} from '../constants/marketPromotion.constants';
import type {
  MpRegionDonationParams,
  MpRegionDonationsSearch,
  MpRegionFilterSearch,
} from '../types/marketPromotion.types';

const route = getRouteApi('/_auth/market-promotion/region-summary/donations');

interface DrillSubject {
  isProject: boolean;
  name: string | undefined;
  isLoading: boolean;
}

interface DrillParams {
  filterSearch: MpRegionFilterSearch & { dateFrom: string; dateTo: string };
  donationParams: MpRegionDonationParams;
}

function toDrillParams(search: MpRegionDonationsSearch): DrillParams {
  const { rsmUserId, projectId, q, ...filters } = search;
  const defaultPeriod = getMonthToDateRange();
  const filterSearch = {
    ...filters,
    dateFrom: filters.dateFrom ?? defaultPeriod.dateFrom,
    dateTo: filters.dateTo ?? defaultPeriod.dateTo,
  };
  return {
    filterSearch,
    donationParams: {
      ...filterSearch,
      rsmUserId,
      promoItemIds: projectId === undefined ? filterSearch.promoItemIds : [projectId],
      search: q,
    },
  };
}

function useDrillSubject(
  filterSearch: MpRegionFilterSearch,
  search: MpRegionDonationsSearch,
): DrillSubject {
  const isProject = search.projectId !== undefined;
  const regionsQuery = useQuery({ ...mpRegionsQueryOptions(filterSearch), enabled: !isProject });
  const projectsQuery = useQuery({
    ...mpRegionProjectsQueryOptions(filterSearch),
    enabled: isProject,
  });

  if (isProject) {
    return {
      isProject,
      name: projectsQuery.data?.items.find((project) => project.promoItemId === search.projectId)
        ?.itemName,
      isLoading: projectsQuery.isLoading,
    };
  }
  return {
    isProject,
    name: regionsQuery.data?.items.find((region) => region.rsmUserId === search.rsmUserId)
      ?.regionName,
    isLoading: regionsQuery.isLoading,
  };
}

function getSummaryPills(
  subject: DrillSubject,
  filterSearch: DrillParams['filterSearch'],
  donationCount: number | undefined,
  amount: number | undefined,
): PillField[] {
  return [
    {
      key: 'subject',
      icon: subject.isProject ? Package : MapPinned,
      label: subject.isProject ? 'Donation Project' : 'Region',
      value: subject.name,
      isLoading: subject.isLoading,
    },
    {
      key: 'period',
      icon: CalendarRange,
      label: 'Dates',
      value: `${formatDisplayDate(filterSearch.dateFrom)} – ${formatDisplayDate(filterSearch.dateTo)}`,
      isLoading: false,
    },
    {
      key: 'donations',
      icon: HandCoins,
      label: 'Donations',
      value: donationCount === undefined ? undefined : formatNumber(donationCount),
      isLoading: donationCount === undefined,
    },
    {
      key: 'amount',
      icon: Banknote,
      label: 'Amount',
      value: amount === undefined ? undefined : `${TAKA_SYMBOL} ${formatNumber(amount)}`,
      isLoading: amount === undefined,
    },
  ];
}

interface MpRegionDonationsPageProps {
  search: MpRegionDonationsSearch;
}

export function MpRegionDonationsPage({ search }: MpRegionDonationsPageProps) {
  const navigate = useNavigate();
  const routeNavigate = route.useNavigate();
  const { filterSearch, donationParams } = useMemo(() => toDrillParams(search), [search]);
  const subject = useDrillSubject(filterSearch, search);
  useRememberViewedRow('donationRegions', search.rsmUserId ?? null);
  useRememberViewedRow('donationProjects', search.projectId ?? null);

  const { searchText, setSearchText } = useUrlSyncedSearch({
    appliedText: search.q,
    onApply: (text) => {
      void routeNavigate({ search: (previous) => ({ ...previous, q: text }), replace: true });
    },
  });

  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(mpRegionDonationsInfiniteQueryOptions(donationParams));
  const columns = useMemo(() => getRegionDonationColumns(search.q), [search.q]);

  const listTab = subject.isProject ? REGION_REPORT_TAB.projects : REGION_REPORT_TAB.regions;

  return (
    <DetailTableLayout
      title={subject.isLoading ? undefined : (subject.name ?? 'Donations')}
      search={{
        value: searchText,
        onChange: setSearchText,
        placeholder: DONATION_SEARCH_PLACEHOLDER,
      }}
      onNoHistory={() =>
        void navigate({ to: REGION_REPORT_PATH, search: { ...filterSearch, tab: listTab } })
      }
      summary={
        <LabeledPillList
          fields={getSummaryPills(
            subject,
            filterSearch,
            firstPageMeta?.total,
            firstPageMeta?.totals.distributedAmount,
          )}
        />
      }
      table={{
        rows,
        columns,
        isLoading,
        error: loadError,
        errorTitle: 'Could not load the donations',
        onRetry: () => void refetch(),
        stickyColumnId: REGION_DONATION_ZM_COLUMN_ID,
        ...getTableEmptyText(search.q, 'No donation in these dates', DETAIL_TABLE_WIDER_DATES_HINT),
        loadMoreProps,
      }}
    />
  );
}
